
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Plus, Trash2, Edit, Save, MoveUp, MoveDown } from "lucide-react";
import { toast } from "sonner";

type StatusManagementProps = {
  companyId: string;
  onStatusesUpdate?: () => Promise<void>;
};

const StatusManagement = ({ companyId, onStatusesUpdate }: StatusManagementProps) => {
  const [statuses, setStatuses] = useState<any[]>([]);
  const [newStatus, setNewStatus] = useState({
    name: "",
    color: "#3b82f6",
    description: ""
  });
  const [editingStatusId, setEditingStatusId] = useState<string | null>(null);
  const [editedStatus, setEditedStatus] = useState({
    name: "",
    color: "",
    description: ""
  });
  const [isLoading, setIsLoading] = useState(true);

  const fetchStatuses = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('company_statuses')
        .select('*')
        .eq('company_id', companyId)
        .order('position', { ascending: true });

      if (error) {
        console.error('Error fetching statuses:', error);
        toast.error('Failed to load statuses');
        return;
      }

      setStatuses(data || []);
    } catch (error) {
      console.error('Error in fetchStatuses:', error);
      toast.error('Failed to load statuses');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (companyId) {
      fetchStatuses();
    }
  }, [companyId]);

  const handleCreateStatus = async () => {
    if (!newStatus.name.trim()) return;
    
    try {
      const nextPosition = statuses.length > 0 
        ? Math.max(...statuses.map(s => s.position)) + 1 
        : 1;

      const { error } = await supabase
        .from('company_statuses')
        .insert({
          company_id: companyId,
          name: newStatus.name,
          color: newStatus.color,
          description: newStatus.description || null,
          position: nextPosition
        });
      
      if (error) {
        console.error('Error creating status:', error);
        toast.error('Failed to create status');
        return;
      }
      
      await fetchStatuses();
      if (onStatusesUpdate) {
        await onStatusesUpdate();
      }
      setNewStatus({
        name: "",
        color: "#3b82f6",
        description: ""
      });
      toast.success('Status created successfully');
    } catch (error) {
      console.error('Error in handleCreateStatus:', error);
      toast.error('Failed to create status');
    }
  };

  const handleStartEditing = (status: any) => {
    setEditingStatusId(status.id);
    setEditedStatus({
      name: status.name,
      color: status.color,
      description: status.description || ""
    });
  };

  const handleSaveEdit = async (statusId: string) => {
    if (!editedStatus.name.trim()) return;
    
    try {
      const { error } = await supabase
        .from('company_statuses')
        .update({
          name: editedStatus.name,
          color: editedStatus.color,
          description: editedStatus.description || null
        })
        .eq('id', statusId);
      
      if (error) {
        console.error('Error updating status:', error);
        toast.error('Failed to update status');
        return;
      }
      
      await fetchStatuses();
      if (onStatusesUpdate) {
        await onStatusesUpdate();
      }
      setEditingStatusId(null);
      toast.success('Status updated successfully');
    } catch (error) {
      console.error('Error in handleSaveEdit:', error);
      toast.error('Failed to update status');
    }
  };

  const handleDeleteStatus = async (statusId: string) => {
    try {
      // Check if this is the only status
      if (statuses.length <= 1) {
        toast.error('Cannot delete the only status. Please create another status first.');
        return;
      }

      const confirmed = window.confirm("Are you sure you want to delete this status?");
      if (!confirmed) return;
      
      const { error } = await supabase
        .from('company_statuses')
        .delete()
        .eq('id', statusId);
      
      if (error) {
        console.error('Error deleting status:', error);
        toast.error('Failed to delete status');
        return;
      }
      
      await fetchStatuses();
      if (onStatusesUpdate) {
        await onStatusesUpdate();
      }
      toast.success('Status deleted successfully');
    } catch (error) {
      console.error('Error in handleDeleteStatus:', error);
      toast.error('Failed to delete status');
    }
  };

  const handleMoveStatus = async (statusId: string, direction: 'up' | 'down') => {
    try {
      const currentIndex = statuses.findIndex(s => s.id === statusId);
      if (currentIndex === -1) return;
      
      const newStatuses = [...statuses];
      
      if (direction === 'up' && currentIndex > 0) {
        // Swap with previous item
        [newStatuses[currentIndex], newStatuses[currentIndex - 1]] = 
          [newStatuses[currentIndex - 1], newStatuses[currentIndex]];
      } else if (direction === 'down' && currentIndex < newStatuses.length - 1) {
        // Swap with next item
        [newStatuses[currentIndex], newStatuses[currentIndex + 1]] = 
          [newStatuses[currentIndex + 1], newStatuses[currentIndex]];
      } else {
        return; // Can't move further in this direction
      }
      
      // Update positions in database
      for (let i = 0; i < newStatuses.length; i++) {
        const { error } = await supabase
          .from('company_statuses')
          .update({ position: i + 1 })
          .eq('id', newStatuses[i].id);
        
        if (error) {
          console.error('Error updating status position:', error);
          toast.error('Failed to reorder statuses');
          return;
        }
      }
      
      await fetchStatuses();
      if (onStatusesUpdate) {
        await onStatusesUpdate();
      }
      toast.success('Statuses reordered successfully');
    } catch (error) {
      console.error('Error in handleMoveStatus:', error);
      toast.error('Failed to reorder statuses');
    }
  };

  if (isLoading) {
    return <div className="text-center py-8">Loading statuses...</div>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Manage Statuses</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <h3 className="font-medium">Current Statuses</h3>
          {statuses.length === 0 ? (
            <div className="text-muted-foreground">No statuses defined yet. Create your first status below.</div>
          ) : (
            <div className="space-y-2">
              {statuses.map((status) => (
                <div
                  key={status.id}
                  className="flex items-center gap-3 p-3 border rounded-md"
                >
                  {editingStatusId === status.id ? (
                    <>
                      <div className="flex-1 grid grid-cols-4 gap-2">
                        <div className="col-span-2">
                          <Input
                            value={editedStatus.name}
                            onChange={(e) => setEditedStatus({ ...editedStatus, name: e.target.value })}
                            placeholder="Status name"
                          />
                        </div>
                        <div>
                          <Input
                            type="color"
                            value={editedStatus.color}
                            onChange={(e) => setEditedStatus({ ...editedStatus, color: e.target.value })}
                          />
                        </div>
                        <div>
                          <Input
                            value={editedStatus.description}
                            onChange={(e) => setEditedStatus({ ...editedStatus, description: e.target.value })}
                            placeholder="Description"
                          />
                        </div>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => setEditingStatusId(null)}>
                        Cancel
                      </Button>
                      <Button size="sm" onClick={() => handleSaveEdit(status.id)}>
                        <Save className="h-4 w-4 mr-1" />
                        Save
                      </Button>
                    </>
                  ) : (
                    <>
                      <div
                        className="w-5 h-5 rounded"
                        style={{ backgroundColor: status.color }}
                      />
                      <div className="flex-1">
                        <div className="font-medium">{status.name}</div>
                        {status.description && (
                          <div className="text-xs text-muted-foreground">
                            {status.description}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center">
                        <Button size="sm" variant="ghost" onClick={() => handleMoveStatus(status.id, 'up')}>
                          <MoveUp className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleMoveStatus(status.id, 'down')}>
                          <MoveDown className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleStartEditing(status)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDeleteStatus(status.id)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-4 border-t pt-4">
          <h3 className="font-medium">Add New Status</h3>
          <div className="grid grid-cols-4 gap-2">
            <div className="col-span-2">
              <Label htmlFor="new-status-name">Name</Label>
              <Input
                id="new-status-name"
                value={newStatus.name}
                onChange={(e) => setNewStatus({ ...newStatus, name: e.target.value })}
                placeholder="New status"
              />
            </div>
            <div>
              <Label htmlFor="new-status-color">Color</Label>
              <Input
                id="new-status-color"
                type="color"
                value={newStatus.color}
                onChange={(e) => setNewStatus({ ...newStatus, color: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="new-status-desc">Description</Label>
              <Input
                id="new-status-desc"
                value={newStatus.description}
                onChange={(e) => setNewStatus({ ...newStatus, description: e.target.value })}
                placeholder="Optional description"
              />
            </div>
          </div>
          <Button onClick={handleCreateStatus}>
            <Plus className="h-4 w-4 mr-2" />
            Add Status
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default StatusManagement;
